import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/take';
import 'rxjs/add/operator/filter';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { of } from 'rxjs/observable/of';
import { type ActionsObservable, combineEpics, type Epic } from 'redux-observable';
import { concat } from 'rxjs/observable/concat';

import { sendTrackAnalyticsEvent } from '../../common/analytics/analytics-web-client';
import { NOT_FOUND } from '../../common/constants/http-status-codes';
import { fetchFailed, fetchSuccess } from '../../common/fetch-request-analytics';
import {
	GET_PROJECT_SPACE_LINK,
	getProjectSpaceLinkError,
	PUT_PROJECT_SPACE_LINK,
	putProjectSpaceLinkError,
	successfullyConnectedSpace,
	fetchConnectedSpace,
	updateProjectSpaceLink,
	updateConnectedSpaceOrPageContent,
	type PutProjectSpaceLinkAction,
	updateConfluenceSpaceConnectionState,
	UPDATE_CONFLUENCE_SPACES,
} from '../actions';
import { getSelectedSpace } from '../ui/connect-space/selectors';
import { getUserEnteredSpaceName } from '../ui/create-space/selectors';

import {
	createProjectSpaceLink$,
	deleteProjectSpaceLink$,
	deleteProjectSpaceLinkWithAri$,
	fetchProjectSpaceLink$,
} from './requests';
import { getCloudId } from '../context/selectors';
import { type Action, type MiddlewareAPI } from 'redux';
import { type State } from '../types';
import { CONNECTED, ERROR, NOT_CONNECTED } from '../confluence/connected-space/types';
import { shouldCreateSpaceForAutoProvisionedConfluence } from '../personalization/selectors';
import { combineLatest } from 'rxjs/observable/combineLatest';
import { UPDATE_PERSONALIZATION_SITE_TRAITS } from '../actions/personalization-site-traits';
import { fg } from '@atlaskit/platform-feature-flags';

const missingCloudId = () => {
	sendTrackAnalyticsEvent({
		source: 'space.create',
		action: 'missing',
		actionSubject: 'cloudId',
		attributes: { errorMessage: 'Missing cloudId in tenant context' },
	});
};

const updateProjectSpaceAnalytics = () => {
	sendTrackAnalyticsEvent({
		source: 'project.space',
		action: 'update',
		actionSubject: 'link',
	});
};

/**
 * Finds the root page of the space intended to be linked to the project and
 * sends a PUT request to xflow to store the project + space link
 * @param action$
 */
const putProjectSpaceLink = (
	action$: ActionsObservable<PutProjectSpaceLinkAction>,
	store: MiddlewareAPI<State>,
) =>
	action$.ofType(PUT_PROJECT_SPACE_LINK).switchMap((action: PutProjectSpaceLinkAction) => {
		const state = store.getState();
		const {
			spaceKey,
			spaceUrl,
			projectKey,
			projectId,
			autoCreated = false,
			spaceId,
			connectedSpaceId,
			connectedPageId,
		} = action;
		const selectedSpace = getSelectedSpace(state);
		const userEnteredSpaceName: string = getUserEnteredSpaceName(state);
		const connectedSpaceOrPageTitle =
			selectedSpace?.linkedPageTitle || selectedSpace?.spaceName || userEnteredSpaceName;
		const connectedSpaceOrPageUrl =
			selectedSpace?.linkedPageUrl || selectedSpace?.spaceUrl || spaceUrl;
		const isConnectedToPage = Boolean(selectedSpace?.linkedPageTitle);
		const isConnectedToLiveDoc = Boolean(selectedSpace?.isLiveDoc);
		const cloudId = getCloudId(state);
		if (!cloudId) {
			missingCloudId();
			return putProjectSpaceLinkError() as any;
		}

		const dispatchOnSuccessActions = () => {
			const updateLinkAction = isConnectedToPage
				? updateConnectedSpaceOrPageContent({
						title: connectedSpaceOrPageTitle,
						url: connectedSpaceOrPageUrl,
						projectSpacePageTitleHasBeenFetched: true,
						isConnectedToPage,
						iconUrl: null,
						pageId: null,
					})
				: fetchConnectedSpace(spaceKey);
			return concat(
				of(
					successfullyConnectedSpace(
						connectedSpaceOrPageTitle,
						isConnectedToPage,
						isConnectedToLiveDoc,
					),
				),
				of(
					updateProjectSpaceLink(
						spaceKey,
						selectedSpace?.linkedPageId ? selectedSpace.linkedPageId : null,
					),
				),
				of(updateLinkAction),
				of(updateConfluenceSpaceConnectionState(spaceKey ? CONNECTED : NOT_CONNECTED)),
			);
		};
		// First create the new project -> space/page association
		return createProjectSpaceLink$({
			linkedPageId: selectedSpace?.linkedPageId ? selectedSpace?.linkedPageId : null,
			cloudId,
			projectId,
			spaceId,
		})
			.switchMap(() => {
				fetchSuccess('put.project.space', {
					spaceKey,
					projectKey,
					autoCreated,
				});
				// If new project -> space/page association succeeds, delete the old project -> space/page association
				return deleteProjectSpaceLink$({
					linkedPageId: connectedPageId ?? null,
					cloudId,
					projectId,
					spaceId: connectedSpaceId!,
				})
					.switchMap(() => {
						fetchSuccess('delete.project.space', {
							spaceKey,
							projectKey,
						});
						return dispatchOnSuccessActions();
					})
					.catch((err) => {
						fetchFailed('delete.project.space', err);
						/**
						 * If this catch block is hit it means the previous association was unable to be deleted, but the new association was still created.
						 * Therefore we dispatch success actions here to ensure the UI is updated to show the newly associated space/page.
						 * This is an edge case and should be quite rare, it is tied to the reliability of AGG / AGS.
						 */
						return dispatchOnSuccessActions();
					});
			})
			.catch((err) => {
				fetchFailed('put.project.space', err, { autoCreated });
				return concat(
					of(putProjectSpaceLinkError()),
					of(updateConfluenceSpaceConnectionState(ERROR)),
				);
			});
	});

/**
 * Sends a GET request to xflow to retrieve the project + space link
 * Note: returned space may have been deleted already. Another issue should handle this.
 * @param action$
 */
const getProjectSpaceLink = (action$: ActionsObservable<any>, store: MiddlewareAPI<State>) =>
	combineLatest(
		action$.ofType(UPDATE_PERSONALIZATION_SITE_TRAITS),
		action$.ofType(UPDATE_CONFLUENCE_SPACES),
		action$.ofType(GET_PROJECT_SPACE_LINK),
	)
		.take(1)
		.switchMap(([traits, spaces, action]) => {
			const state = store.getState();
			const { projectId } = action;
			const cloudId = getCloudId(state);

			if (!cloudId) {
				throw new Error('Error fetching project space link: missing cloudId in tenant context');
			}
			return fetchProjectSpaceLink$(cloudId, projectId)
				.switchMap(({ spaceKey, linkedPageId, arisOfRelationshipsToDelete }) => {
					// If there is a corrupt existing relationship to a page or space, delete the relationship and display the "not connected" state to allow the user to conenct a new space
					if (arisOfRelationshipsToDelete && fg('project_pages_delete_corrupt_relationships')) {
						const projectAri = buildProjectAri(cloudId, projectId).projectAri;
						const deleteOperations = arisOfRelationshipsToDelete.map((ari) =>
							deleteProjectSpaceLinkWithAri$(projectAri, ari).catch((err) => {
								fetchFailed('delete.project.space', err);
								return of(null);
							}),
						);

						return forkJoin(deleteOperations).switchMap(() => {
							fetchSuccess('delete.project.space');
							return concat(
								of(updateProjectSpaceLink(null, null)),
								of(updateConfluenceSpaceConnectionState(NOT_CONNECTED)),
							);
						});
					}

					fetchSuccess('fetch.project.space');
					updateProjectSpaceAnalytics();
					return concat([
						updateProjectSpaceLink(spaceKey || null, linkedPageId || null),
						updateConfluenceSpaceConnectionState(spaceKey ? CONNECTED : NOT_CONNECTED),
					]);
				})
				.catch((err: any) => {
					if (err.statusCode === NOT_FOUND) {
						// no project space stored previously. Treat as success and clear confluence space link
						fetchSuccess('updated.previously.empty.project.space');

						const isAutoProvisionFlow = shouldCreateSpaceForAutoProvisionedConfluence(state);
						return concat([
							updateProjectSpaceLink(null, null),
							...(!isAutoProvisionFlow
								? [updateConfluenceSpaceConnectionState(NOT_CONNECTED)]
								: []),
						]) as any;
					}

					fetchFailed('fetch.project.space', err);
					return of(getProjectSpaceLinkError());
				});
		});

const buildProjectAri = (cloudId: string, projectId: string): { projectAri: string } => {
	const projectAri = `ari:cloud:jira:${cloudId}:project/${projectId}`;
	return { projectAri };
};

export default combineEpics(putProjectSpaceLink, getProjectSpaceLink) as Epic<Action, State>;
